export type ImpulseCategory = "quote" | "tip" | "habit";

export interface FinancialImpulse {
  id: number;
  category: ImpulseCategory;
  content: string;
  author?: string;
  source?: string;
}

export const financialImpulses: FinancialImpulse[] = [
  // ── Quotes ───────────────────────────────────────────────────────────────
  {
    id: 1,
    category: "quote",
    content: "Do not save what is left after spending; instead spend what is left after saving.",
    author: "Warren Buffett",
  },
  {
    id: 2,
    category: "quote",
    content: "The stock market is a device for transferring money from the impatient to the patient.",
    author: "Warren Buffett",
  },
  {
    id: 3,
    category: "quote",
    content: "An investment in knowledge pays the best interest.",
    author: "Benjamin Franklin",
  },
  {
    id: 4,
    category: "quote",
    content: "Wealth is not about having a lot of money; it's about having a lot of options.",
    author: "Chris Rock",
  },
  {
    id: 5,
    category: "quote",
    content: "The goal isn't more money. The goal is living life on your own terms.",
    author: "Chris Brogan",
  },
  {
    id: 6,
    category: "quote",
    content: "It's not how much money you make, but how much money you keep, how hard it works for you, and how many generations you keep it for.",
    author: "Robert Kiyosaki",
    source: "Rich Dad Poor Dad",
  },
  {
    id: 7,
    category: "quote",
    content: "Financial freedom is available to those who learn about it and work for it.",
    author: "Robert Kiyosaki",
    source: "Rich Dad Poor Dad",
  },
  {
    id: 8,
    category: "quote",
    content: "The secret to wealth is simple: find a way to do more for others than anyone else does.",
    author: "Tony Robbins",
  },
  {
    id: 9,
    category: "quote",
    content: "Spend less than you earn, invest the rest, and avoid debt. Do this long enough and you'll be wealthy.",
    author: "JL Collins",
    source: "The Simple Path to Wealth",
  },
  {
    id: 10,
    category: "quote",
    content: "The best thing money can buy is financial freedom.",
    author: "Rob Berger",
  },
  {
    id: 11,
    category: "quote",
    content: "Never spend your money before you have it.",
    author: "Thomas Jefferson",
  },
  {
    id: 12,
    category: "quote",
    content: "Compound interest is the eighth wonder of the world. He who understands it, earns it; he who doesn't, pays it.",
    author: "Albert Einstein (attributed)",
  },
  {
    id: 13,
    category: "quote",
    content: "The habit of saving is itself an education; it fosters every virtue, teaches self-denial, cultivates the sense of order, trains to forethought, and so broadens the mind.",
    author: "T.T. Munger",
  },
  {
    id: 14,
    category: "quote",
    content: "Price is what you pay. Value is what you get.",
    author: "Warren Buffett",
  },
  {
    id: 15,
    category: "quote",
    content: "The real measure of your wealth is how much you'd be worth if you lost all your money.",
    author: "Unknown",
  },
  {
    id: 16,
    category: "quote",
    content: "Wealth consists not in having great possessions, but in having few wants.",
    author: "Epictetus",
  },
  {
    id: 17,
    category: "quote",
    content: "You must gain control over your money or the lack of it will forever control you.",
    author: "Dave Ramsey",
  },
  {
    id: 18,
    category: "quote",
    content: "A budget is telling your money where to go instead of wondering where it went.",
    author: "Dave Ramsey",
  },
  {
    id: 19,
    category: "quote",
    content: "The more you learn, the more you earn.",
    author: "Warren Buffett",
  },
  {
    id: 20,
    category: "quote",
    content: "Saving is a great habit but without investing and tracking, it just sleeps.",
    author: "Manoj Arora",
    source: "From the Rat Race to Financial Freedom",
  },
  {
    id: 21,
    category: "quote",
    content: "Time in the market beats timing the market.",
    author: "Common investing principle",
  },
  {
    id: 22,
    category: "quote",
    content: "The person who doesn't know where his next dollar is coming from usually doesn't know where his last dollar went.",
    author: "Unknown",
  },
  {
    id: 23,
    category: "quote",
    content: "Building wealth is not a get-rich-quick scheme. It's about making sensible decisions over time.",
    author: "Morgan Housel",
    source: "The Psychology of Money",
  },
  {
    id: 24,
    category: "quote",
    content: "Doing well with money has little to do with how smart you are and a lot to do with how you behave.",
    author: "Morgan Housel",
    source: "The Psychology of Money",
  },
  {
    id: 25,
    category: "quote",
    content: "The ability to do what you want, when you want, with who you want, for as long as you want to, is priceless. It is the highest dividend money pays.",
    author: "Morgan Housel",
    source: "The Psychology of Money",
  },
  {
    id: 26,
    category: "quote",
    content: "Risk is what's left over when you think you've thought of everything.",
    author: "Carl Richards",
  },
  {
    id: 27,
    category: "quote",
    content: "I will tell you the secret to getting rich: be fearful when others are greedy and greedy when others are fearful.",
    author: "Warren Buffett",
  },
  // ── Tips ─────────────────────────────────────────────────────────────────
  {
    id: 28,
    category: "tip",
    content: "Pay yourself first. Automate a savings transfer on the day you receive your paycheck — before you have a chance to spend it.",
  },
  {
    id: 29,
    category: "tip",
    content: "Build an emergency fund covering 3–6 months of living expenses before focusing on investments.",
  },
  {
    id: 30,
    category: "tip",
    content: "The 50/30/20 rule: allocate 50% of income to needs, 30% to wants, and 20% to savings and debt repayment.",
  },
  {
    id: 31,
    category: "tip",
    content: "When shopping, wait 48 hours before making any non-essential purchase over a set amount. Impulse buying fades fast.",
  },
  {
    id: 32,
    category: "tip",
    content: "Always negotiate. Salaries, bills, insurance premiums — most prices are more flexible than they appear.",
  },
  {
    id: 33,
    category: "tip",
    content: "Max out tax-advantaged accounts (pension, ISA, 401k, Roth) before investing in taxable accounts.",
  },
  {
    id: 34,
    category: "tip",
    content: "Diversify your investments across asset classes (stocks, bonds, real estate) to reduce risk over time.",
  },
  {
    id: 35,
    category: "tip",
    content: "Track your net worth monthly, not just your bank balance. Net worth = assets − liabilities.",
  },
  {
    id: 36,
    category: "tip",
    content: "High-interest debt (credit cards, payday loans) should be paid off before investing — guaranteed return beats market uncertainty.",
  },
  {
    id: 37,
    category: "tip",
    content: "Use cash-back or rewards credit cards for everyday spending — but only if you pay the balance in full every month.",
  },
  {
    id: 38,
    category: "tip",
    content: "Increase your savings rate whenever you get a raise. If you never see the extra income, you won't miss it.",
  },
  {
    id: 39,
    category: "tip",
    content: "Consider the true hourly cost of a purchase: divide the price by your hourly take-home wage. Is it worth that many hours of your life?",
  },
  {
    id: 40,
    category: "tip",
    content: "Set a monthly 'fun money' budget. Guilt-free spending within the limit is healthier than white-knuckling every purchase.",
  },
  {
    id: 41,
    category: "tip",
    content: "Understand the difference between an asset (puts money in your pocket) and a liability (takes money out of your pocket).",
  },
  {
    id: 42,
    category: "tip",
    content: "Before investing, write down your goal, timeline, and risk tolerance. Clarity prevents panic-selling during downturns.",
  },
  {
    id: 43,
    category: "tip",
    content: "Low-cost index funds consistently outperform the majority of actively managed funds over the long term.",
  },
  {
    id: 44,
    category: "tip",
    content: "Automate bill payments to avoid late fees and protect your credit score.",
  },
  {
    id: 45,
    category: "tip",
    content: "Re-evaluate insurance annually. You may be over-insured in some areas and under-insured in others.",
  },
  // ── Habits ───────────────────────────────────────────────────────────────
  {
    id: 46,
    category: "habit",
    content: "Do a monthly subscription audit. Cancel anything you haven't used in the past 30 days.",
  },
  {
    id: 47,
    category: "habit",
    content: "Set one no-spend day each week. Use it to cook at home, enjoy free activities, and reset your relationship with spending.",
  },
  {
    id: 48,
    category: "habit",
    content: "Log every expense for 30 days — including the small ones. Awareness is the first step to control.",
  },
  {
    id: 49,
    category: "habit",
    content: "Hold a monthly 'money date' with yourself (or your partner). Review your budget, progress, and upcoming expenses.",
  },
  {
    id: 50,
    category: "habit",
    content: "Read at least one personal finance book per quarter. Knowledge compounds just like money.",
  },
  {
    id: 51,
    category: "habit",
    content: "Meal plan each week. It cuts grocery bills, reduces food waste, and eliminates daily decision fatigue.",
  },
  {
    id: 52,
    category: "habit",
    content: "Set a specific savings goal with a deadline. Vague goals don't get funded — concrete ones do.",
  },
  {
    id: 53,
    category: "habit",
    content: "Review your financial goals every Sunday evening. Five minutes of intention prevents months of drift.",
  },
  {
    id: 54,
    category: "habit",
    content: "Unsubscribe from retail marketing emails. Out of sight, out of cart.",
  },
  {
    id: 55,
    category: "habit",
    content: "Before any large purchase, sleep on it for a week. If you still want it after 7 days, it's probably not just an impulse.",
  },
  {
    id: 56,
    category: "habit",
    content: "Batch similar errands to save on fuel and time. Small efficiencies compound into meaningful savings.",
  },
  {
    id: 57,
    category: "habit",
    content: "Celebrate financial milestones — debt paid off, savings target hit — with a modest reward. Progress should feel good.",
  },
  {
    id: 58,
    category: "habit",
    content: "Check your accounts at least once a week. Regular visibility keeps you honest and catches errors early.",
  },
  {
    id: 59,
    category: "habit",
    content: "Put windfalls (bonuses, tax refunds, gifts) straight into savings or debt before they blend into everyday spending.",
  },
  {
    id: 60,
    category: "habit",
    content: "Write down three things you're grateful for about your current financial situation. Gratitude reduces the urge to overspend.",
  },
];
